import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DetailsPageParams } from 'ts/hotel/interfaces/Generic';
import {
  addLogs,
  beginOneHotelSearch,
  getHotelBook,
  getHotelInfo,
  getOneHotelSearchData,
} from 'services/hotel';
import { Hotel, HotelRoomId, RoomClass } from 'ts/hotel/interfaces/HotelDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleLeft, faLocationDot, faSearch } from '@fortawesome/free-solid-svg-icons';
import { checkAHAVI, showError } from 'utils/utils';
import RoomDetails from './RoomsDetails';
import DetailSkeleton, { RoomDetailsSkeleton } from './DetailsSkeleton';
import DetailsMap from '../Maps/DetailsMap';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import * as idb from 'utils/shared/idbUrl';
import LoaderMain from 'components/shared/LoaderMain';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import ShowMoreLess from 'components/shared/ShowMoreLess';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import ExpiredToken from 'components/shared/ExpiredToken';
import { dateFormat } from 'utils/shared/utils';
import { useGetPage } from 'hooks/useGetPage';

import ImageGallery from 'react-image-gallery';
import { HotelInfoResponseData } from 'ts/hotel/interfaces/HotelInfoResponse';
import DetailsSummary from './DetailsSummary';
import { clearRoomNamePatterns, getHotelIndex, getKey, matchesHotelUkey } from 'utils/hotel';

import { formatPriceHotel } from 'utils/hotel';
import { useHotelProposal } from 'hooks/useProposal';
import { RoomTypeGrp } from 'ts/hotel/classes/FilterModel';
import { HotelDetailsContext } from 'contexts/HotelDetailsContext';
import { SerpDetails } from './SerpDetails';
import useDataLayer from 'hooks/useHotelDataLayer';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b } from 'constants/generic';

const DetailsPage: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [bookLoading, setBookLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<HotelInfoResponseData>();
  const [oneDataHotel, setOneDataHotel] = useState<Hotel | undefined>();
  const [poolingFinished, setPoolingFinished] = useState(false);
  const preview = searchParams.get('preview') === 'true';
  const { hotelIndex } = useContext(HotelConfigContext);
  const { userSiteConfig } = useContext(MainContext);
  const currency = sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd';
  const detailsRef = useRef<HTMLDivElement>(null);
  const detWrRef = useRef<HTMLDivElement>(null);
  const fetchAbortController = useRef(new AbortController());
  const timeout = useRef<any>();
  const location = useLocation();
  const roomsRef = useRef<HTMLDivElement>(null);
  const [roomTypes, setRoomTypes] = useState<RoomTypeGrp[]>([]);
  const { toggleHotelProposal, isHotelInProposal } = useHotelProposal();
  const idbKey = location.pathname + location.search;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => saveState());
  const scrollIntervalRef = useRef<any>(null);
  const [serpLoaded, setSerpLoaded] = useState(false);
  const { pushHotelProductDetails } = useDataLayer();
  const stateRef = useRef<any>({});
  const loadedSearchRef = useRef<string>();
  const detailsKeyRef = useRef(idbKey);
  const dataRef = useRef(data);
  const oneDataHotelRef = useRef(oneDataHotel);
  const backModule = 'hotel' + getHotelIndex(hotelIndex);
  const backHome = '/hotels' + getHotelIndex(hotelIndex);
  const rememberMode = !!userSiteConfig.base.rememberModeEnabled;
  const isDetailsLocation =
    location.pathname.includes('/results') || location.pathname.includes('/details');
  dataRef.current = data;
  oneDataHotelRef.current = oneDataHotel;
  if (isDetailsLocation) {
    detailsKeyRef.current = idbKey;
  }
  stateRef.current = {
    data,
    oneDataHotel,
    roomTypes,
    poolingFinished,
    locationSearch: isDetailsLocation ? location.search : stateRef.current.locationSearch,
  };
  useGetPage('Hotels/Details');

  useEffect(() => {
    const hotelIdx = getHotelIndex(hotelIndex);
    const isResultsWithDetails =
      location.pathname === `/hotels${hotelIdx}/results` && !!searchParams.get('details');
    const isStandaloneDetails = location.pathname === `/hotel${hotelIdx}/details`;

    if (!isResultsWithDetails && !isStandaloneDetails) {
      return;
    }

    if (b2b) {
      const shouldLoadModal = isResultsWithDetails && !userSiteConfig.base.openDetailsInNewTab;
      const shouldLoadStandalone = isStandaloneDetails;
      if (!shouldLoadModal && !shouldLoadStandalone) {
        return;
      }

      fetchAbortController.current.abort();
      clearTimeout(timeout.current);
      clearScrollInterval();

      const modalContent = detWrRef.current?.closest('.modal-content') as HTMLElement | null;
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
    }

    if (
      rememberMode &&
      loadedSearchRef.current === location.search &&
      dataRef.current?.hotelDetails &&
      oneDataHotelRef.current
    ) {
      setPoolingFinished(true);
      return;
    }

    let cancelled = false;

    (async () => {
      const searchParamsObj = Object.fromEntries(searchParams) as unknown as DetailsPageParams;
      const params = (
        b2b && isResultsWithDetails
          ? Object.fromEntries(new URLSearchParams(searchParamsObj.details))
          : searchParamsObj
      ) as DetailsPageParams & { roomBToken?: string };

      const cachedState = await idb.getPageState(idbKey);
      if (cancelled) return;

      const canRestore =
        rememberMode &&
        cachedState &&
        location.search === cachedState.locationSearch &&
        cachedState.oneDataHotel &&
        matchesHotelUkey(cachedState.oneDataHotel, params.id);

      if (canRestore) {
        setData(cachedState.data);
        setOneDataHotel(cachedState.oneDataHotel);
        setRoomTypes(cachedState.roomTypes || []);
        setPoolingFinished(cachedState.poolingFinished ?? true);
        loadedSearchRef.current = location.search;
        setTimeout(() => idb.deletePageState(idbKey), 500);
        return;
      }

      idb.deletePageState(idbKey);
      loadedSearchRef.current = undefined;
      setData(undefined);
      setRoomTypes([]);
      setOneDataHotel(undefined);
      setPoolingFinished(false);

      const formattedParams = {
        searchToken: params.session,
        hotelUkey: params.id,
        roomBToken: params.roomBToken || '',
      };
      const hotelResponse = await getHotelInfo(formattedParams, hotelIndex);
      if (cancelled) return;

      if (hotelResponse?.sessionExpired) {
        setData({ sessionExpired: true } as any);
      } else if (hotelResponse && !hotelResponse.hasErrors && hotelResponse.hotelDetails) {
        setData(hotelResponse);
      }

      const hotelFromSearch = JSON.parse(
        sessionStorage.getItem(getKey('hotelFromSearch', hotelIndex)) || '{}'
      );
      const roomsBelongToHotel = matchesHotelUkey(hotelFromSearch, params.id);
      const roomsStr = roomsBelongToHotel
        ? sessionStorage.getItem(getKey('multiRoomsBTokens', hotelIndex))
        : null;
      const roomsParams = JSON.parse(roomsStr || '[]') as HotelRoomId[];
      const hotelItemCode =
        hotelResponse?.hotelDetails?.hotel?.itemCode || hotelFromSearch?.itemCode || '';
      const newParams = roomsParams.length
        ? roomsParams.map((room: HotelRoomId) => ({
          multiRoomsBTokens: room.multiRoomsBTokens,
          roomBToken: room.multiRoomsBTokens[0],
          hotelItemCode: room.hotelItemCode,
        }))
        : params.roomBToken && hotelItemCode
          ? [
            {
              multiRoomsBTokens: [params.roomBToken],
              roomBToken: params.roomBToken,
              hotelItemCode,
            },
          ]
          : [];

      if (!newParams.length) {
        loadedSearchRef.current = location.search;
        setPoolingFinished(true);
        return;
      }

      const beginOneHotelResponse = await beginOneHotelSearch({
        hotels: newParams,
        searchToken: params.session,
      });
      if (cancelled) return;

      if (beginOneHotelResponse?.searchToken) {
        fetchAbortController.current = new AbortController();
        sessionStorage.setItem(
          getKey('searchTokenRooms', hotelIndex),
          beginOneHotelResponse.searchToken
        );
        loadedSearchRef.current = location.search;
        getOneHotel(beginOneHotelResponse.searchToken);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]); // eslint-disable-line

  const clearScrollInterval = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearScrollInterval();
  }, [location.pathname]);

  const getOneHotel = async (searchToken: string) => {
    const oneHotelResponse = await getOneHotelSearchData(searchToken, fetchAbortController.current);

    const thbUsdRate = sessionStorage.getItem('thbUsdRate');

    if (oneHotelResponse?.hotels?.length) {
      const allRooms = oneHotelResponse.hotels.reduce((acc: RoomClass[], h) => {
        const newRC = h.hotel.roomClasses
          .filter((rc, index, arr) => {
            return (
              arr.findIndex(
                (r) =>
                  r.hotelRooms[0].desc === rc.hotelRooms[0].desc &&
                  r.cxl.limitDate === rc.cxl.limitDate &&
                  r.price.usd === rc.price.usd &&
                  r.board.basis.code === rc.board.basis.code
              ) === index
            );
          })
          .map((rc) => {
            return {
              ...rc,
              htuKey: h.hotel.htuKey,
              suppCode: h.hotel.item.sysSuppCode,
              suppDesc:
                (h.hotel.item.sysSupp ? h.hotel.item.sysSupp : '') ||
                (h.hotel.item.sysSuppCode ? h.hotel.item.sysSuppCode : ''),
            };
          });
        return [...acc, ...newRC];
      }, []);

      const allRoomsFormatted = allRooms
        .map((room) => {
          let displayRoomName = '';
          const result = room.hotelRooms.map((hotelRoom) => {
            const displayName = `${room.desc ? room.desc + ' - ' : ''}${hotelRoom.desc ? hotelRoom.desc : ''
              } ${room.board.basis.desc ? room.board.basis.desc : ''}`;
            displayRoomName += displayName + ' ';
            let clearedName = displayName;
            clearRoomNamePatterns.forEach((pattern) => {
              clearedName = clearedName.replaceAll(pattern, '');
            });
            clearedName = clearedName.replaceAll(/[-:,]\s*/g, ' ').trim();
            return {
              ...hotelRoom,
              displayName,
              displayNameCleared: clearedName,
            };
          });

          const displayDate =
            new Date(room.cxl.limitDate) < new Date()
              ? t('hotel.non_refundable')
              : dateFormat(room.cxl.limitDate, 'dd MMM-yy');

          const thb = room.price.usd / parseFloat(thbUsdRate || '1');
          const newPrice = { ...room.price, thb };
          const displayPrice = formatPriceHotel(newPrice, currency);

          return {
            ...room,
            price: newPrice,
            hotelRooms: result,
            displayName: displayRoomName,
            displayDate,
            displayPrice,
            visible: true,
            visibleSm: true,
          };
        })
        .sort((a, b) => a.price.usd - b.price.usd);

      if (allRoomsFormatted.length !== oneDataHotel?.roomClasses.length) {
        setOneDataHotel((prev) => {
          if (allRoomsFormatted.length !== prev?.roomClasses.length) {
            return {
              ...oneHotelResponse.hotels[0].hotel,
              roomClasses: allRoomsFormatted,
            };
          }

          return prev;
        });
      }

      setData((prev) => {
        if (!prev && oneHotelResponse) {
          const newHotel = oneHotelResponse.hotels[0].hotel;
          const newData = {
            hotelDetails: {
              hotel: newHotel as any,
              images: newHotel.images.map((img) => ({
                link: img.imageLink,
                alt: '',
              })),
              description: newHotel.info.hotelAttributes,
              hotelFacilities: newHotel.hotFacilities,
            },
          } as HotelInfoResponseData;

          return newData;
        }
        return prev;
      });
    }

    if (!oneHotelResponse?.poolingFinished) {
      timeout.current = setTimeout(async () => {
        await getOneHotel(searchToken);
      }, 2000);
    } else {
      if (!oneHotelResponse?.hotels?.length) {
        addLogs(JSON.stringify('no_rooms_found' + oneHotelResponse?.requestResponse));
      }
      setPoolingFinished(true);
    }
  };

  const onClickBook = async (room: RoomClass) => {
    if (bookLoading) return;
    setBookLoading(true);

    const params = {
      id: room?.htuKey || '',
      session: sessionStorage.getItem(getKey('searchTokenRooms', hotelIndex)) || '',
      room: room?.rcUniqueKey,
    };

    const book = await getHotelBook(params);
    setBookLoading(false);

    if (book?.hotel?.oneHotelSearchFailed) {
      showError(t('hotel.one_hotel_search_failed'));
    } else if (!book?.hasErrors && book?.hotel && book?.roomClass) {
      const thbUsdRate = sessionStorage.getItem('thbUsdRate');
      sessionStorage.setItem(
        getKey('detailsHotel', hotelIndex),
        JSON.stringify({
          ...book,
          hotel: {
            ...book.hotel,
            roomClasses: [],
          },
          roomClass: { ...book.roomClass, price: { ...book.roomClass.price, thb: book.roomClass?.price?.usd / parseFloat(thbUsdRate || '1') } }
        })
      );
      await saveState();

      pushHotelProductDetails(book?.hotel);
      navigate(
        `/hotel${getHotelIndex(hotelIndex)}/book?${new URLSearchParams(params as {}).toString()}`
      );
    } else {
      showError(book?.errorMessage || 'Error');
    }
  };

  const scrollToElement = () => {
    const { pathname } = location;
    const normalizedPath = pathname.toLowerCase();
    const hasHotel = normalizedPath.includes('hotel');
    const hasDetails = normalizedPath.includes('details');
    const hasResults = normalizedPath.includes('results');

    if (!hasHotel || (!hasDetails && !hasResults)) {
      return;
    }

    const offset = 150;
    const speed = 10;
    const stepDivider = 300;
    const modalContent = detailsRef.current?.closest('.modal-content');

    if (roomsRef.current && modalContent && detailsRef.current) {
      const elementPosition = roomsRef.current.getBoundingClientRect().top + modalContent.scrollTop;
      const targetPosition = elementPosition - offset;

      const distance = targetPosition - modalContent.scrollTop;
      const step = distance / stepDivider;

      const intervalId = setInterval(() => {
        const currentScroll = modalContent.scrollTop;

        if (Math.abs(targetPosition - currentScroll) <= Math.abs(step)) {
          modalContent.scrollTop = targetPosition;
          clearInterval(intervalId);
        } else if (currentScroll > targetPosition) {
          clearInterval(intervalId);
        } else {
          modalContent.scrollTop += step;
        }
      }, speed);
    } else if (roomsRef.current) {
      const elementPosition = roomsRef.current.getBoundingClientRect().top + window.scrollY;
      const targetPosition = elementPosition - offset;
      const initialScroll = window.scrollY;
      if (initialScroll >= targetPosition) {
        return;
      }

      const distance = targetPosition - initialScroll;

      const step = distance / stepDivider;

      const intervalId = setInterval(() => {
        const currentScroll = window.scrollY;
        if (currentScroll >= targetPosition) {
          clearScrollInterval();
          return;
        }

        if (Math.abs(targetPosition - currentScroll) <= Math.abs(step)) {
          window.scrollTo(0, targetPosition);
          clearScrollInterval();
        } else {
          window.scrollBy(0, step);
        }
      }, speed);

      scrollIntervalRef.current = intervalId;
    }
  };
  const saveState = async () => {
    if (!rememberMode) return;
    await idb.savePageState(detailsKeyRef.current, stateRef.current);
  };

  return (
    <HotelDetailsContext.Provider value={{ roomTypes, setRoomTypes }}>
      <div className={'det-' + getHotelIndex(hotelIndex)} ref={detWrRef}>
        {!!data?.hotelDetails?.hotel && (
          <div
            className="details-wrapper hotel-details-wrapper flex-column"
            style={{ flexDirection: 'column' }}
            ref={detailsRef}
          >
            {window.history.length > 1 && (
              <div className="mb-20 default-shadow flex-between dyn-change-header w100p">
                <Button
                  title={t('back')}
                  icon={faArrowCircleLeft}
                  onClick={() => onClickBack(backModule, 'results')}
                  className="btn btn-link"
                />
                <Button
                  title={t('search.new')}
                  icon={faSearch}
                  onClick={() => onClickSearch(backModule, backHome)}
                  className="btn btn-link"
                />
              </div>
            )}
            <div className="flex-between w100p">
              <div className="w100p"></div>
              <div className="details">
                <div className="position-relative">
                  <DetailsSummary hotelDetails={data.hotelDetails} oneHotel={oneDataHotel} />
                  <div className="details-slider default-shadow position-relative">
                    <div className="details-slider-header title-header-grey">
                      {!!oneDataHotel && (
                        <ProposalHeart
                          onClickPropsal={() => toggleHotelProposal(oneDataHotel)}
                          active={isHotelInProposal(oneDataHotel)}
                        />
                      )}
                      <div>
                        <div className="title">{data.hotelDetails.hotel.item?.name}</div>
                        <div className="subtitle">
                          <FontAwesomeIcon icon={faLocationDot} />
                          {data.hotelDetails.hotel.addressInfo?.address}
                        </div>
                      </div>
                      <Rating value={data.hotelDetails.hotel.item?.star || 0} />
                    </div>
                    {!!data.hotelDetails.images?.length && (
                      <div className="details-slider-images">
                        <ImageGallery
                          items={data.hotelDetails.images.slice(0, 25).map((image) => ({
                            original: image.link,
                            thumbnail: image.link,
                            originalAlt: image.alt,
                            thumbnailAlt: image.alt,
                          }))}
                          showThumbnails={true}
                          showPlayButton={false}
                          showFullscreenButton={false}
                          thumbnailPosition="right"
                        />
                      </div>
                    )}
                  </div>
                  {data.hotelDetails.description && (
                    <div
                      className="details-block default-shadow position-relative"
                      id="hotel-overview"
                      style={{ zIndex: 1 }}
                    >
                      <div className="title-underline text-lg mb-20 title-header-grey">
                        {t('hotel.overview')}
                      </div>
                      <div
                        className={
                          'text-lh' + (checkAHAVI(data.hotelDetails.description) ? ' rtl' : ' ltr')
                        }
                        dangerouslySetInnerHTML={{ __html: data.hotelDetails.description }}
                      ></div>
                      <ShowMoreLess selector=".details-block .text-lh" maxHeight={200} />
                    </div>
                  )}
                </div>

                <div className="position-relative" id="rooms-details">
                  {!preview && !oneDataHotel && !poolingFinished && (
                    <div ref={roomsRef} onLoad={scrollToElement}>
                      <RoomDetailsSkeleton />
                    </div>
                  )}
                  {!preview && !oneDataHotel && poolingFinished && (
                    <div
                      className="details-block default-shadow text-center flex-center"
                      style={{ height: 150 }}
                    >
                      {t('hotel.rooms_were_not_found')}
                    </div>
                  )}
                  {!preview && oneDataHotel && (
                    <RoomDetails
                      rooms={oneDataHotel.roomClasses}
                      setRooms={(roomClasses) =>
                        setOneDataHotel((prev) => ({ ...prev!, roomClasses }))
                      }
                      hotel={oneDataHotel}
                      poolingFinished={poolingFinished}
                      bookLoading={bookLoading}
                      onClickBook={onClickBook}
                    />
                  )}
                </div>
                <div className="position-relative" style={{ minHeight: 400 }}>
                  <div
                    className={
                      'details-block details-map default-shadow details-map-serp' +
                      (serpLoaded ? ' ' : '')
                    }
                  >
                    <div className="title-underline text-lg mb-20 title-header-grey">
                      {t('hotel.location')}
                    </div>
                    {!!data.hotelDetails.hotel.position && (
                      <DetailsMap
                        lat={data.hotelDetails.hotel.position.latitude}
                        lng={data.hotelDetails.hotel.position.longitude}
                        zoom={16.5}
                      />
                    )}
                  </div>
                  <SerpDetails hotel={data.hotelDetails.hotel} onLoad={() => setSerpLoaded(true)} />
                </div>
              </div>
            </div>
            {/* {(!b2b || !isResultPage) && } */}
          </div>
        )}

        {data?.sessionExpired && <ExpiredToken storePath="hotel" />}

        {data?.hasErrors && (
          <div className="expired">
            <div className="title">{t('something_went_wrong')}</div>
            <p className="expired-content">Hotel was not found</p>
            <Link to={'/'} className="btn btn-primary">
              {t('home.go_back')}
            </Link>
          </div>
        )}

        {!data && <DetailSkeleton />}

        {bookLoading && (
          <LoaderMain systemProducts="Hotels" systemProductsDetails="LoadingBook" />
        )}

      </div>
    </HotelDetailsContext.Provider>
  );
};

export default withErrorBoundary(DetailsPage, 'DetailsPage');
